import React, { useEffect, useState } from 'react'
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import KeyIcon from '@mui/icons-material/Key'
import * as S from './styles'
import { Container } from 'src/ui'
import useLogin from './Login.State'
import ForgotPassword from './ForgotPassword'
import MessageSent from './MessageSent'
import MobileDesktopNotification from '../MobileDesktopNotification'
import SetupMfa from '../Mfa/SetupMfa'
import { useAuth } from 'src/common/context'
import { useNavigate } from 'react-router-dom'

const Login: React.FC = () => {
  const { state, submitEmail, usePasskeyButton, openTryAnother, usePassword, submitPassword, backToEmail } = useLogin()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [emailInput, setEmailInput] = useState('')
  const [passwordInput, setPasswordInput] = useState('')
  const [modals, setModals] = useState({ forgotPassword: false, messageSent: false })

  const handleClose = (modal: string) => setModals((s) => ({ ...s, [modal]: false }))
  const handleOpen = (modal: string) => setModals((s) => ({ ...s, [modal]: true }))

  useEffect(() => {
    if (user) navigate('/')
  }, [user])

  // Desktop-only app.
  const isMobileDevice = /android|iphone|kindle|ipad/i.test(navigator.userAgent)
  if (isMobileDevice) {
    return <MobileDesktopNotification />
  }

  const onKey = (e: React.KeyboardEvent, fn: () => void) => {
    if (e.key === 'Enter') fn()
  }

  return (
    <>
      <Container title="Log in to your account" error={state.error || undefined}>
        {state.step === 'email' && (
          <>
            <S.Row>
              <S.InputLabel>Email *</S.InputLabel>
              <TextField
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => onKey(e, () => submitEmail(emailInput))}
                placeholder="Email"
                fullWidth
                autoFocus
              />
            </S.Row>
            <Button
              variant="contained"
              fullWidth
              size="large"
              loading={state.loading}
              onClick={() => submitEmail(emailInput)}
            >
              Continue
            </Button>
          </>
        )}

        {state.step === 'passkey' && (
          <>
            <Typography variant="body2" color="grey.600" sx={{ mb: 2 }}>
              Use your passkey to finish signing in as <strong>{state.email}</strong>.
            </Typography>

            {state.loading ? (
              <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Waiting for your passkey…</Typography>
              </Box>
            ) : (
              <>
                <Button variant="contained" fullWidth size="large" startIcon={<KeyIcon />} onClick={usePasskeyButton}>
                  Use your passkey
                </Button>

                {state.tryAnotherOpen ? (
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ mt: 2, backgroundColor: 'common.white', '&:hover': { backgroundColor: 'common.white' } }}
                    onClick={usePassword}
                  >
                    Use your password instead
                  </Button>
                ) : (
                  <Box textAlign="center" sx={{ mt: 2 }}>
                    <S.StyledLink component="a" onClick={openTryAnother}>
                      Try another way
                    </S.StyledLink>
                  </Box>
                )}
              </>
            )}

            <Box textAlign="center" sx={{ mt: 3 }}>
              <S.StyledLink component="a" onClick={backToEmail}>
                Use a different email
              </S.StyledLink>
            </Box>
          </>
        )}

        {state.step === 'password' && (
          <>
            <Typography variant="body2" color="grey.600" sx={{ mb: 2 }}>
              Signing in as <strong>{state.email}</strong>.{' '}
              <S.StyledLink component="a" onClick={backToEmail}>
                Change
              </S.StyledLink>
            </Typography>
            <S.InputLabel>Password *</S.InputLabel>
            <TextField
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyDown={(e) => onKey(e, () => submitPassword(passwordInput))}
              type="password"
              placeholder="Password"
              fullWidth
              autoFocus
            />
            <S.ActionsRow>
              <S.StyledLink component="a" onClick={() => handleOpen('forgotPassword')}>
                Forgot Password?
              </S.StyledLink>
              <S.Button variant="contained" loading={state.loading} onClick={() => submitPassword(passwordInput)}>
                Log In
              </S.Button>
            </S.ActionsRow>
          </>
        )}
      </Container>

      <ForgotPassword open={modals.forgotPassword} handleClose={handleClose} handleOpen={handleOpen} />
      <MessageSent open={modals.messageSent} handleClose={handleClose} />
      {state.setupMfa && <SetupMfa open={state.setupMfa} setupMfaParams={state.setupMfaParams} enforced />}
    </>
  )
}

export default Login
